// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Resources } from '@shared/localizations/Resources';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { OperationType } from '@shared/utils/Trading/OperationType';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { RiskPlan } from '@shared/commons/cache/RiskPlan';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { DateTimeConvertor } from '@shared/utils/Time/DateTimeConvertor';
import { OrderUtils } from '@shared/utils/Trading/OrderUtils';
import { FilledOrderFormatter } from '@shared/commons/cache/Formatters/FilledOrderFormatter';
import { StringUtils } from '@shared/utils/StringUtils';
import { IsAllowed } from '@shared/commons/IsAllowed';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
import { ThemeManager } from '../misc/ThemeManager';
export class FilledOrderItem extends BaseItem {
    constructor(trade) {
        super();
        this.trade = trade;
        if ((trade === null || trade === void 0 ? void 0 : trade.InstrumentStr) != null) {
            const [instrumentStr] = trade.InstrumentStr.split(':');
            trade.InstrumentStr = instrumentStr;
        }
    }
    getQuickTableCellForeground(column) {
        const colors = ThemeManager.colors;
        switch (column) {
            case FilledOrderItem.INSTRUMENT_COL_INDEX:
                return colors.tableSymbolForeground;
            case FilledOrderItem.AMOUNT_COL_INDEX:
                return colors.tableQuantityForeground;
            case FilledOrderItem.OPERATION_COL_INDEX:
                if (!isNullOrUndefined(this.trade)) {
                    return this.trade.BuySell === OperationType.Buy ? colors.buyColor : colors.sellColor;
                }
                else {
                    return super.getQuickTableCellForeground(column);
                }
            default:
                return super.getQuickTableCellForeground(column);
        }
    }
    ColumnCount() {
        return FilledOrderItem.columnsParams.length;
    }
    getColumnData(column) {
        var _a, _b, _c, _d;
        const trade = this.trade;
        const instrument = this.GetCurrentInstrument();
        const value = this.getColumnValue(column);
        let formattedValue = null;
        switch (column) {
            case FilledOrderItem.TRADE_ID_COL_INDEX:
                formattedValue = trade.TradeId;
                break;
            case FilledOrderItem.OPERATION_COL_INDEX:
                if (trade.BuySell === OperationType.Buy) {
                    formattedValue = Resources.getResource('panel.watchlist.Buy');
                }
                else if (trade.BuySell === OperationType.Sell) {
                    formattedValue = Resources.getResource('panel.watchlist.Sell');
                }
                else {
                    //    formattedValue =  Const.getBuySellStr(trade.BuySell);
                    // TODO
                    formattedValue = trade.BuySell;
                }
                break;
            case FilledOrderItem.AMOUNT_COL_INDEX:
                formattedValue = value.toString();
                break;
            case FilledOrderItem.PRICE_COL_INDEX: {
                formattedValue = instrument !== null ? instrument.formatPricePrecision(value) : value.toString();
                break;
            }
            case FilledOrderItem.COMMISSION_COL_INDEX:
                formattedValue = FilledOrderFormatter.ExecutionFeeStr(trade);
                break;
            case FilledOrderItem.REALIZED_PL_COL_INDEX:
                formattedValue = FilledOrderFormatter.GrossPnLStr(trade);
                break;
            case FilledOrderItem.TIME_COL_INDEX:
                formattedValue = DateTimeUtils.FormatToTime(value); // .ToLongTimeString();
                break;
            case FilledOrderItem.DATE_COL_INDEX:
                formattedValue = DateTimeUtils.formatDate(value, 'DD.MM.YYYY  HH:mm:ss'); // .ToShortDateString();
                break;
            case FilledOrderItem.EXTERNAL_PRICE_COL_INDEX:
                if (isNaN(value)) {
                    formattedValue = '';
                }
                else {
                    if (instrument !== null) {
                        formattedValue = instrument.formatPrice(value);
                    }
                    else {
                        // TODO
                        formattedValue = value != null ? value.toString() : '';
                    }
                }
                break;
            case FilledOrderItem.PURCHASE_PRICE_COL_INDEX:
                formattedValue = FilledOrderFormatter.ExposureStr(trade);
                break;
            case FilledOrderItem.NET_PL_COL_INDEX:
                formattedValue = FilledOrderFormatter.NetPnLStr(trade);
                break;
            case FilledOrderItem.BOUGHT_COL_INDEX:
                formattedValue = FilledOrderFormatter.GetBoughtStr(trade);
                break;
            case FilledOrderItem.SOLD_COL_INDEX:
                formattedValue = FilledOrderFormatter.GetSoldStr(trade);
                break;
            case FilledOrderItem.REBATES_COL_INDEX:
                formattedValue = FilledOrderFormatter.RebatesStr(trade);
                break;
            case FilledOrderItem.STRIKE_PRICE_COL_INDEX:
                if (value > 0) {
                    if (instrument != null) {
                        formattedValue = instrument.formatPrice(value);
                    }
                    else {
                        formattedValue = value.toString();
                    }
                }
                else {
                    formattedValue = '';
                }
                break;
            case FilledOrderItem.EXPIRATION_DATE_COL_INDEX:
                if ((value === null || value === void 0 ? void 0 : value.getTime()) > 0) {
                    formattedValue = DateTimeUtils.FormatToDate(value);
                }
                else {
                    formattedValue = '';
                }
                break;
            case FilledOrderItem.TRADE_ID_LABEL_COL_INDEX:
                formattedValue = (_b = (_a = trade.TradeIdLabel) !== null && _a !== void 0 ? _a : trade.TradeId) !== null && _b !== void 0 ? _b : '';
                break;
            case FilledOrderItem.COMMISSION_IN_QUOTING_CURRENCY_COL_INDEX:
            case FilledOrderItem.REALIZED_PL_IN_QUOTING_CURRENCY_COL_INDEX:
            case FilledOrderItem.NET_PL_IN_QUOTING_CURRENCY_COL_INDEX:
                if (column === FilledOrderItem.COMMISSION_IN_QUOTING_CURRENCY_COL_INDEX &&
                    ((_c = trade === null || trade === void 0 ? void 0 : trade.AdvancedTradeParams) === null || _c === void 0 ? void 0 : _c.CommissionAssetID) != null) {
                    const asset = instrument !== null
                        ? instrument.DataCache.GetAssetById(trade.AdvancedTradeParams.CommissionAssetID.Value)
                        : null;
                    formattedValue = asset !== null ? asset.formatPrice(value) : value.toString();
                }
                if (instrument !== null) {
                    if (value != null) {
                        const exp2Asset = instrument.DataCache.GetAssetByName(instrument.Exp2);
                        formattedValue =
                            exp2Asset !== null
                                ? exp2Asset.formatPrice(value)
                                : value.toString() + ' ' + instrument.Exp2;
                    }
                    else {
                        formattedValue = instrument.Exp2;
                    }
                }
                else {
                    // TODO
                    if (value != null) {
                        formattedValue = value.toString();
                    }
                    else {
                        // TODO// System.String.Format("{0:N2}", value);
                        formattedValue = '';
                    }
                }
                break;
            case FilledOrderItem.EXECUTION_VENUE_COL_INDEX:
                if (trade.ExecutionVenue != null) {
                    formattedValue = trade.ExecutionVenue;
                }
                else {
                    if ((instrument === null || instrument === void 0 ? void 0 : instrument.TradingExchange) != null) {
                        // #89156
                        formattedValue = instrument.TradingExchange;
                    }
                    else {
                        formattedValue = '';
                    }
                }
                break;
            case FilledOrderItem.LEVERAGE_COL_INDEX:
                formattedValue = (_d = OrderUtils.GetFormattedLeverage(value, true)) !== null && _d !== void 0 ? _d : StringUtils.NOT_AVAILABLE;
                break;
            default:
                formattedValue = value != null ? value.toString() : '';
                break;
        }
        return new ColumnData(value, formattedValue);
    }
    getColumnValue(column, useAccCurrency = null) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
        const trade = this.trade;
        if (trade == null) {
            return null;
        }
        const ins = this.GetCurrentInstrument();
        switch (column) {
            case FilledOrderItem.TRADE_ID_COL_INDEX:
                return FilledOrderFormatter.TradeId(trade);
            case FilledOrderItem.ORDER_ID_COL_INDEX:
                return FilledOrderFormatter.IdStr(trade);
            case FilledOrderItem.INSTRUMENT_COL_INDEX:
                return FilledOrderFormatter.SymbolStr(trade);
            case FilledOrderItem.ACCOUNT_COL_INDEX:
                return FilledOrderFormatter.AccountStr(trade);
            case FilledOrderItem.OPERATION_COL_INDEX:
                return trade.BuySell;
            case FilledOrderItem.AMOUNT_COL_INDEX:
                return FilledOrderFormatter.Quantity(trade);
            case FilledOrderItem.PRICE_COL_INDEX:
                return FilledOrderFormatter.Price(trade);
            case FilledOrderItem.COMMISSION_COL_INDEX:
                return FilledOrderFormatter.ExecutionFee(trade, useAccCurrency);
            case FilledOrderItem.REALIZED_PL_COL_INDEX:
                return FilledOrderFormatter.GrossPnL(trade, useAccCurrency);
            case FilledOrderItem.TIME_COL_INDEX:
                return trade.Time;
            // TODO
            // return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(trade.Time, BaseApplication.App.MultiDataCache !== null ? BaseApplication.App.MultiDataCache.DCache : trade.DataCache);
            case FilledOrderItem.DATE_COL_INDEX:
                return trade.Time;
            // TODO
            // return DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(trade.Time, BaseApplication.App.MultiDataCache !== null ? BaseApplication.App.MultiDataCache.DCache : trade.DataCache);
            case FilledOrderItem.EXTERNAL_TRADE_ID_COL_INDEX:
                return trade.ExternalTradeId;
            case FilledOrderItem.LOGIN_COL_INDEX:
                return trade.Login;
            case FilledOrderItem.ROUTE_COL_INDEX:
                return trade.RouteID;
            case FilledOrderItem.EXTERNAL_PRICE_COL_INDEX:
                return trade.ExternalPrice;
            case FilledOrderItem.PURCHASE_PRICE_COL_INDEX:
                return FilledOrderFormatter.Exposure(trade, useAccCurrency);
            case FilledOrderItem.EXCHANGE_TRADING_COL_INDEX:
                return (_a = FilledOrderFormatter.TradingExchangeStr(trade)) !== null && _a !== void 0 ? _a : ins === null || ins === void 0 ? void 0 : ins.TradingExchange;
            case FilledOrderItem.ORDER_TYPE_COL_INDEX:
                return FilledOrderFormatter.OrderTypeStr(trade);
            case FilledOrderItem.INSTR_TYPE_COL_INDEX:
                if (ins != null) {
                    return InstrumentUtils.getInstrumentTypeStringLocalized(ins.InstrType);
                }
                else {
                    return (_b = trade.SymbolType) !== null && _b !== void 0 ? _b : '';
                }
            case FilledOrderItem.NET_PL_COL_INDEX:
                return FilledOrderFormatter.NetPnL(trade, useAccCurrency);
            case FilledOrderItem.BOUGHT_COL_INDEX:
                return ins != null ? FilledOrderFormatter.GetBought(trade) : 0;
            case FilledOrderItem.SOLD_COL_INDEX:
                return ins != null ? FilledOrderFormatter.GetSold(trade) : 0;
            case FilledOrderItem.REBATES_COL_INDEX:
                return FilledOrderFormatter.Rebates(trade, useAccCurrency);
            case FilledOrderItem.STRIKE_PRICE_COL_INDEX:
                return (trade === null || trade === void 0 ? void 0 : trade.StrikePrice) > 0 ? trade.StrikePrice : 0;
            case FilledOrderItem.EXPIRATION_DATE_COL_INDEX:
                return (ins === null || ins === void 0 ? void 0 : ins.ExpDateReal.getFullYear()) > 2000
                    ? DateTimeConvertor.ConvertUTCTimeToSelectedTimeZone(ins.ExpDateReal)
                    : DateTimeUtils._ZeroTime;
            case FilledOrderItem.EXTERNAL_ORDER_ID_COL_INDEX:
                return (_c = trade.ExternalOrderId) !== null && _c !== void 0 ? _c : '';
            case FilledOrderItem.TRADE_ID_LABEL_COL_INDEX:
                return parseInt((_e = (_d = trade.TradeIdLabel) === null || _d === void 0 ? void 0 : _d.split('-')[0]) !== null && _e !== void 0 ? _e : trade.TradeId.split('-')[0]);
            case FilledOrderItem.COMMISSION_IN_QUOTING_CURRENCY_COL_INDEX:
                return -trade.ComissionInInstrumentQuotingCurrency;
            case FilledOrderItem.REALIZED_PL_IN_QUOTING_CURRENCY_COL_INDEX:
                return trade.PnLInInstrumentQuotingCurrency;
            case FilledOrderItem.NET_PL_IN_QUOTING_CURRENCY_COL_INDEX:
                if (trade !== null) {
                    return (trade.PnLInInstrumentQuotingCurrency - trade.ComissionInInstrumentQuotingCurrency + trade.Swap);
                }
                else {
                    return 0;
                }
            case FilledOrderItem.PRODUCT_TYPE_COL_INDEX:
                return FilledOrderFormatter.ProductTypeStr(trade);
            case FilledOrderItem.EXECUTION_VENUE_COL_INDEX:
                return (_g = (_f = trade.ExecutionVenue) !== null && _f !== void 0 ? _f : ins === null || ins === void 0 ? void 0 : ins.TradingExchange) !== null && _g !== void 0 ? _g : '';
            case FilledOrderItem.DESCRIPTION_COL_INDEX:
                return (_j = (_h = trade.Description) !== null && _h !== void 0 ? _h : ins === null || ins === void 0 ? void 0 : ins.DescriptionValue()) !== null && _j !== void 0 ? _j : null;
            case FilledOrderItem.LEVERAGE_COL_INDEX:
                return (_k = trade === null || trade === void 0 ? void 0 : trade.Leverage) !== null && _k !== void 0 ? _k : null;
            default:
                return null;
        }
    }
    getFormatTotalValue(column, value, totalCurrencyAfterFiltration = null) {
        var _a, _b;
        const asset = this.GetDataCache().GetAssetByName(totalCurrencyAfterFiltration);
        let formattedValue = '';
        switch (column) {
            case FilledOrderItem.AMOUNT_COL_INDEX:
                formattedValue = value.toFixed(2);
                break;
            case FilledOrderItem.COMMISSION_COL_INDEX:
            case FilledOrderItem.REALIZED_PL_COL_INDEX:
            case FilledOrderItem.NET_PL_COL_INDEX:
            case FilledOrderItem.PURCHASE_PRICE_COL_INDEX:
            case FilledOrderItem.REBATES_COL_INDEX:
                formattedValue = (_b = (_a = asset === null || asset === void 0 ? void 0 : asset.formatPrice(value)) !== null && _a !== void 0 ? _a : value === null || value === void 0 ? void 0 : value.toFixed(2)) !== null && _b !== void 0 ? _b : '';
                break;
            case FilledOrderItem.PRICE_COL_INDEX:
                formattedValue = this.trade.Instrument.formatPricePrecision(value, false, false);
                break;
            default:
                formattedValue = super.getFormatTotalValue(column, value, totalCurrencyAfterFiltration);
                break;
        }
        return formattedValue;
    }
    GetColumnParams(column) {
        const result = FilledOrderItem.columnsParams[column];
        result.Hidden = result.DefaultHidden || Resources.isHidden(result.HeaderKey);
        result.MenuIndex = this.GetMenuIndex(result.HeaderKey);
        switch (column) {
            case FilledOrderItem.TIME_COL_INDEX:
            case FilledOrderItem.ROUTE_COL_INDEX:
            case FilledOrderItem.EXTERNAL_PRICE_COL_INDEX:
            case FilledOrderItem.EXECUTION_VENUE_COL_INDEX:
                result.Hidden = true;
                break;
            case FilledOrderItem.PRODUCT_TYPE_COL_INDEX:
                result.Hidden = !RiskPlan.showProductType;
                break;
            case FilledOrderItem.PRICE_COL_INDEX:
                result.aggregateVisibility = IsAllowed.IsAggregatedPriceAllowed()
                    ? TableTotalsVisibility.Groups
                    : TableTotalsVisibility.None;
                break;
        }
        return result;
    }
    fireUpdate() { }
    GetCurrentAccount() {
        var _a, _b;
        return (_b = (_a = this.trade) === null || _a === void 0 ? void 0 : _a.Account) !== null && _b !== void 0 ? _b : null;
    }
    GetCurrentInstrument() {
        var _a, _b;
        return (_b = (_a = this.trade) === null || _a === void 0 ? void 0 : _a.Instrument) !== null && _b !== void 0 ? _b : null;
    }
    InstrumentName() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.trade) === null || _a === void 0 ? void 0 : _a.Instrument) === null || _b === void 0 ? void 0 : _b.GetInteriorID()) !== null && _c !== void 0 ? _c : null;
    }
    getTotalValuePrecision(column, totalCurrencyAfterFiltration) {
        var _a;
        const asset = this.GetDataCache().GetAssetByName(totalCurrencyAfterFiltration);
        switch (column) {
            case FilledOrderItem.AMOUNT_COL_INDEX:
                return 2;
            case FilledOrderItem.COMMISSION_COL_INDEX:
            case FilledOrderItem.REALIZED_PL_COL_INDEX:
            case FilledOrderItem.NET_PL_COL_INDEX:
            case FilledOrderItem.PURCHASE_PRICE_COL_INDEX:
            case FilledOrderItem.REBATES_COL_INDEX:
                return (_a = asset === null || asset === void 0 ? void 0 : asset.Point) !== null && _a !== void 0 ? _a : 2;
                break;
            case FilledOrderItem.PRICE_COL_INDEX:
                return InstrumentUtils.MAX_PRECISION_ON_SERVER;
            default:
                return super.getTotalValuePrecision(column, totalCurrencyAfterFiltration);
        }
    }
    NeedSortMenu() {
        return true;
    }
    GetMenuIndex(headerKey) {
        if (FilledOrderItem.ListMenuIndex.Contains(headerKey)) {
            return FilledOrderItem.ListMenuIndex.indexOf(headerKey);
        }
        else {
            return 1000;
        }
    }
    GetDataCache() {
        var _a;
        const ins = this.GetCurrentInstrument();
        return ins != null ? ins.DataCache : (_a = this.GetCurrentAccount()) === null || _a === void 0 ? void 0 : _a.DataCache;
    }
}
FilledOrderItem.TRADE_ID_COL_INDEX = 0;
FilledOrderItem.ORDER_ID_COL_INDEX = 1;
FilledOrderItem.INSTRUMENT_COL_INDEX = 2;
FilledOrderItem.ACCOUNT_COL_INDEX = 3;
FilledOrderItem.OPERATION_COL_INDEX = 4;
FilledOrderItem.AMOUNT_COL_INDEX = 5;
FilledOrderItem.PRICE_COL_INDEX = 6;
FilledOrderItem.COMMISSION_COL_INDEX = 7;
FilledOrderItem.REALIZED_PL_COL_INDEX = 8;
FilledOrderItem.TIME_COL_INDEX = 9;
FilledOrderItem.DATE_COL_INDEX = 10;
FilledOrderItem.EXTERNAL_TRADE_ID_COL_INDEX = 11;
FilledOrderItem.LOGIN_COL_INDEX = 12;
FilledOrderItem.ROUTE_COL_INDEX = 13;
FilledOrderItem.EXTERNAL_PRICE_COL_INDEX = 14;
FilledOrderItem.PURCHASE_PRICE_COL_INDEX = 15;
FilledOrderItem.EXCHANGE_TRADING_COL_INDEX = 16;
FilledOrderItem.ORDER_TYPE_COL_INDEX = 17;
FilledOrderItem.INSTR_TYPE_COL_INDEX = 18;
FilledOrderItem.NET_PL_COL_INDEX = 19;
FilledOrderItem.BOUGHT_COL_INDEX = 20;
FilledOrderItem.SOLD_COL_INDEX = 21;
FilledOrderItem.REBATES_COL_INDEX = 22;
FilledOrderItem.STRIKE_PRICE_COL_INDEX = 23;
FilledOrderItem.EXPIRATION_DATE_COL_INDEX = 24;
FilledOrderItem.EXTERNAL_ORDER_ID_COL_INDEX = 25;
FilledOrderItem.TRADE_ID_LABEL_COL_INDEX = 26;
FilledOrderItem.COMMISSION_IN_QUOTING_CURRENCY_COL_INDEX = 27;
FilledOrderItem.REALIZED_PL_IN_QUOTING_CURRENCY_COL_INDEX = 28;
FilledOrderItem.NET_PL_IN_QUOTING_CURRENCY_COL_INDEX = 29;
FilledOrderItem.PRODUCT_TYPE_COL_INDEX = 30;
FilledOrderItem.EXECUTION_VENUE_COL_INDEX = 31;
FilledOrderItem.DESCRIPTION_COL_INDEX = 32;
FilledOrderItem.LEVERAGE_COL_INDEX = 33;
FilledOrderItem.columnsParams = [
    /* 0 */ new ColumnParams('panel.NewTrades.TradeID', 75, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.OrderId', 75, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.NewTrades.Instrument', 75, QTAlignment.Left, TableTotalsVisibility.Groups, true, true),
    new ColumnParams('panel.NewTrades.Account', 75, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.NewTrades.Operation', 75, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.NewTrades.Amount', 75, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    /* 6 */ new ColumnParams('panel.NewTrades.Price', 75, QTAlignment.Right, IsAllowed.IsAggregatedPriceAllowed() ? TableTotalsVisibility.Groups : TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.NewTrades.Commission', 75, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.NewTrades.RealizedProfitLoss', 75, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.NewTrades.Time', 75, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    /* 10 */ new ColumnParams('panel.NewTrades.Date', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.NewTrades.ExternalTradeId', 75, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.Login', 75, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.Route', 75, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.ExternalPrice', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    /* 15 */ new ColumnParams('panel.NewTrades.PurchasePrice', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.NewTrades.ExchangeTrading', 75, QTAlignment.Left, TableTotalsVisibility.None, false),
    new ColumnParams('panel.NewTrades.OrderType', 65, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    new ColumnParams('panel.NewTrades.instrType', 70, QTAlignment.Left, TableTotalsVisibility.None, true, true),
    new ColumnParams('panel.NewTrades.Net_PL', 75, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    /* 20 */ new ColumnParams('panel.NewTrades.Bought', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.Sold', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.Rebates', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.NewTrades.StrikePrice', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.ExpirationDate', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    /* 25 */ new ColumnParams('panel.NewTrades.ExternalOrderId', 75, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.TradeIDLabel', 75, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.NewTrades.Commission.InInstrumentQuotingCurrency', 75, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.NewTrades.RealizedProfitLoss.InInstrumentQuotingCurrency', 75, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.NewTrades.Net_PL.InInstrumentQuotingCurrency', 75, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    /* 30 */ new ColumnParams('panel.NewTrades.ProductType', 75, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.ExecutionVenue', 75, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.Description', 70, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.NewTrades.Leverage', 70, QTAlignment.Left, TableTotalsVisibility.None, false, true)
];
FilledOrderItem.ListMenuIndex = [
    'panel.NewTrades.Account',
    'panel.NewTrades.Login',
    'panel.NewTrades.Route',
    'panel.NewTrades.Instrument',
    'panel.NewTrades.instrType',
    'panel.NewTrades.TradeID',
    'panel.NewTrades.OrderId',
    'panel.NewTrades.ExternalTradeId',
    'panel.NewTrades.Operation',
    'panel.NewTrades.OrderType',
    'panel.NewTrades.Price',
    'panel.NewTrades.Amount',
    'panel.NewTrades.Date',
    'panel.NewTrades.Time',
    'panel.NewTrades.Commission',
    'panel.NewTrades.Rebates',
    'panel.NewTrades.RealizedProfitLoss',
    'panel.NewTrades.Net_PL',
    'panel.NewTrades.PurchasePrice',
    'panel.NewTrades.Bought',
    'panel.NewTrades.Sold',
    'panel.NewTrades.StrikePrice',
    'panel.NewTrades.ExpirationDate',
    'panel.NewTrades.ExternalOrderId'
];
